import * as React from "react";
import { ClassNames, css } from "@emotion/core";
import {
  ControlGroup,
  FormGroup,
  H5,
  InputGroup,
  Position,
} from "@blueprintjs/core";
import { format } from "date-fns";
import {
  DateInput,
  TimePrecision,
  TimePicker,
  TimezoneSelect,
  TimezoneDisplayFormat,
} from "@blueprintjs/datetime";

interface SurveyDetailProps {
  date: Date | null;
  setDate: (date: Date | null) => void;
  timezone: string | undefined;
  setTimezone: (tz: string | undefined) => void;
  start: Date | null;
  setStartTime: (time: Date | null) => void;
  end: Date | null;
  setEndTime: (time: Date | null) => void;
  setCenter: (center: [number, number]) => void;
  center: [number, number];
}

const SurveyDetail: React.FC<SurveyDetailProps> = ({
  date,
  setDate,
  timezone,
  setTimezone,
  start,
  setStartTime,
  end,
  setEndTime,
  setCenter,
  center,
}) => {
  const [lng, setLng] = React.useState(center[0]);
  const [lat, setLat] = React.useState(center[1]);

  React.useEffect(() => {
    setLng(center[0]);
    setLat(center[1]);
  }, [center, setLng, setLat]);

  const applyDate = React.useCallback(
    (date) => {
      const time = start || new Date();

      const hours = time.getHours();
      const minutes = time.getMinutes();
      const seconds = time.getSeconds();

      const dateWithTime = new Date(date);

      dateWithTime.setHours(hours);
      dateWithTime.setMinutes(minutes);
      dateWithTime.setSeconds(seconds);

      return dateWithTime;
    },
    [start]
  );

  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      <H5>Survey Details</H5>
      <ClassNames>
        {({ css }) => (
          <div
            css={css`
              display: flex;
            `}
          >
            <div
              css={css`
                margin-right: 1rem;
              `}
            >
              <ControlGroup fill vertical>
                <div
                  css={css`
                    display: flex;
                  `}
                >
                  <FormGroup
                    disabled={false}
                    label="Survey Longitude"
                    labelFor="survey-longitude"
                    labelInfo="(required)"
                    css={css`
                      margin-right: 1rem;
                    `}
                  >
                    <InputGroup
                      id="survey-longitude"
                      placeholder="Longitude"
                      type="number"
                      value={lng}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setLng(event.target.value);
                      }}
                      onBlur={() => setCenter([lng, lat])}
                      onKeyPress={(event) => {
                        if (event.key === "Enter") {
                          setCenter([lng, lat]);
                        }
                      }}
                    />
                  </FormGroup>
                  <FormGroup
                    disabled={false}
                    label="Survey Latitude"
                    labelFor="survey-latitude"
                    labelInfo="(required)"
                  >
                    <InputGroup
                      id="survey-latitude"
                      placeholder="Latitude"
                      type="number"
                      value={lat}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setLat(event.target.value);
                      }}
                      onBlur={() => setCenter([lng, lat])}
                      onKeyPress={(event) => {
                        if (event.key === "Enter") {
                          setCenter([lng, lat]);
                        }
                      }}
                    />
                  </FormGroup>
                </div>
              </ControlGroup>
              <ControlGroup fill vertical>
                <div
                  css={css`
                    display: flex;
                    width: 100%;
                  `}
                >
                  <FormGroup
                    disabled={false}
                    label="Nominal Survey Date"
                    labelFor="survey-date"
                    labelInfo="(required)"
                    css={css`
                      margin-right: 1rem;
                    `}
                  >
                    <DateInput
                      closeOnSelection
                      canClearSelection={false}
                      formatDate={(date) => format(date, "yyyy-MM-dd")}
                      parseDate={(date) => {
                        if (date) {
                          return applyDate(date);
                        }

                        return null;
                      }}
                      onChange={(date) => setDate(applyDate(date))}
                      placeholder={"YYYY-MM-DD"}
                      value={date.toISOString()}
                      popoverProps={{ position: Position.TOP }}
                      fill
                    />
                  </FormGroup>
                  {start && (
                    <FormGroup
                      disabled={false}
                      label="Start Time"
                      labelFor="survey-start-time"
                      labelInfo="(required)"
                      css={css`
                        margin-right: 1rem;
                      `}
                    >
                      <TimePicker
                        value={start}
                        onChange={(time) => setStartTime(time)}
                        autoFocus={false}
                        disabled={false}
                        precision={TimePrecision.MINUTE}
                        selectAllOnFocus={false}
                        showArrowButtons={false}
                        useAmPm={true}
                      />
                    </FormGroup>
                  )}
                  {end && (
                    <FormGroup
                      disabled={false}
                      label="End Time"
                      labelFor="survey-end-time"
                      labelInfo="(required)"
                      css={css`
                        margin-right: 1rem;
                      `}
                    >
                      <TimePicker
                        value={end}
                        onChange={(time) => setEndTime(time)}
                        autoFocus={false}
                        disabled={false}
                        precision={TimePrecision.MINUTE}
                        selectAllOnFocus={false}
                        showArrowButtons={false}
                        useAmPm={true}
                      />
                    </FormGroup>
                  )}
                </div>
                {date && (
                  <FormGroup
                    disabled={false}
                    label="Timezone"
                    labelFor="survey-tz"
                    labelInfo="(required)"
                  >
                    <TimezoneSelect
                      showLocalTimezone={true}
                      valueDisplayFormat={TimezoneDisplayFormat.COMPOSITE}
                      value={timezone}
                      onChange={setTimezone}
                      popoverProps={{
                        position: Position.BOTTOM,
                      }}
                      date={date}
                    />
                  </FormGroup>
                )}
              </ControlGroup>
            </div>
          </div>
        )}
      </ClassNames>
    </div>
  );
};

export default SurveyDetail;
